/* ==========================================================================
   DEEN - Men's Mix & Match
   Namespaced behaviour: one global (window.DeenMMMen), every hook data-dmm-*,
   so this script can never drive the women's Mix & Match or any other page.

   What it owns:
   - the tabs (filter the family grid, then scroll the chosen family into view)
   - the manual colour switcher (each colour is its own Shopify product; a
     swatch click swaps the card's product, image, price and real size variants)
   - bundle state keyed on the real product id, so several colours of one model
     can sit in the bundle at the same time
   - the summary, progress and savings display
   - adding the chosen variant ids to the cart

   It never invents a variant, never assumes a size and never writes a price
   it was not given by Liquid.
   ========================================================================== */

window.DeenMMMen = (function () {
  "use strict";
  var MONEY_FALLBACK = "USD";
  function activeCurrency(fallback) {
    return (
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      fallback ||
      MONEY_FALLBACK
    );
  }
  function money(cents, currency) {
    var code = activeCurrency(currency),
      amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || void 0, {
        style: "currency",
        currency: code,
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + " " + code;
    }
  }
  function init(root) {
    if (!root || root.dataset.dmmReady === "1") return null;
    root.dataset.dmmReady = "1";
    var MAX = parseInt(root.dataset.dmmSize, 10) || 3,
      CURRENCY = root.dataset.dmmCurrency || MONEY_FALLBACK,
      SHOP_CURRENCY = root.dataset.dmmShopCurrency || CURRENCY,
      CART_URL = root.dataset.dmmCartUrl || "/cart",
      SAVINGS_MODE = root.dataset.dmmSavingsMode || "off",
      items = [],
      busy = !1,
      els = {
        tabs: root.querySelectorAll("[data-dmm-tab]"),
        panels: root.querySelectorAll("[data-dmm-panel]"),
        cards: root.querySelectorAll("[data-dmm-card]"),
        slots: root.querySelector("[data-dmm-slots]"),
        count: root.querySelectorAll("[data-dmm-count]"),
        fills: root.querySelectorAll("[data-dmm-fill]"),
        tracks: root.querySelectorAll("[data-dmm-track]"),
        summarySub: root.querySelector("[data-dmm-summary-sub]"),
        total: root.querySelectorAll("[data-dmm-total]"),
        savings: root.querySelectorAll("[data-dmm-savings]"),
        giftZone: root.querySelector("[data-dmm-gift-zone]"),
        giftStep: root.querySelector("[data-dmm-gift-step]"),
        giftChosen: root.querySelector("[data-dmm-gift-chosen]"),
        giftImg: root.querySelector("[data-dmm-gift-img]"),
        giftColour: root.querySelector("[data-dmm-gift-colour]"),
        giftSize: root.querySelector("[data-dmm-gift-size-label]"),
        bundlePrice: root.querySelectorAll("[data-dmm-bundle-price]"),
        saveAmount: root.querySelectorAll("[data-dmm-save-amount]"),
        savePercent: root.querySelectorAll("[data-dmm-save-percent]"),
        ctas: root.querySelectorAll("[data-dmm-cta]"),
        bar: root.querySelector("[data-dmm-bar]"),
        barSlots: root.querySelector("[data-dmm-bar-slots]"),
        barMeta: root.querySelector("[data-dmm-bar-meta]"),
        barSave: root.querySelector("[data-dmm-bar-save]"),
        toast: root.querySelector("[data-dmm-toast]"),
        live: root.querySelector("[data-dmm-live]"),
        done: root.querySelectorAll("[data-dmm-done]"),
        miles: root.querySelectorAll("[data-dmm-miles]"),
        deal: root.querySelector("[data-dmm-deal]"),
        dealBody: root.querySelector("[data-dmm-deal-body]"),
      },
      toastTimer = null;

    /* Men's Kaching deal. Empty by default, which means the cart is sent exactly
       as before: plain variants, no properties, no discount. Once a real men's
       deal exists, its identifiers go into the theme settings and the same lines
       carry the properties the Kaching function reads, so the ladder on the page
       becomes the ladder in the cart and at checkout. There is no fallback to
       any other deal: a missing identifier simply means off. */
    var KACHING = {
      on: root.dataset.dmmKachingMode === "on",
      deal: (root.dataset.dmmKachingDeal || "").trim(),
      bid: (root.dataset.dmmKachingBid || "").trim(),
      bid2: (root.dataset.dmmKachingBid2 || "").trim(),
      gift: (root.dataset.dmmKachingGift || "").trim(),
      rounding: (root.dataset.dmmKachingRounding || "").trim(),
      version: (root.dataset.dmmKachingVersion || "").trim(),
      market: parseInt(root.dataset.dmmKachingMarket, 10) || 0,
      property: (root.dataset.dmmKachingProperty || "__kaching_bundles").trim(),
    };

    function kachingReady() {
      return !!(KACHING.on && KACHING.deal && KACHING.bid && KACHING.version);
    }

    /* One fresh group id per bundle, which is how Kaching keeps two bundles in
       one cart apart. Same shape the store's existing bundle code uses. */
    function kachingGroupId() {
      var alphabet = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ",
        out = "",
        i;
      for (i = 0; i < 4; i++) {
        out += alphabet.charAt(Math.floor(Math.random() * alphabet.length));
      }
      return out;
    }

    /* Every successful add gets one id of its own, stamped on each of its
       lines. The men's discount function groups by this and by nothing else,
       so two bundles sitting in the same cart each keep their own tier instead
       of pooling into a bigger one. The underscore keeps it out of the cart and
       checkout UI while leaving it readable by the function. */
    var MM_GROUP_PROPERTY = "_deen_mm_group";

    function mmGroupId() {
      return (
        "mm_" +
        Date.now().toString(36) +
        Math.random().toString(36).slice(2, 8)
      );
    }

    /* The tier the bundle is claiming. Two paid units is the 10% bar, three is
       the 10%-plus-legging bar. Anything else is not a tier, so nothing is
       claimed and the cart goes out plain. */
    function kachingBar(units) {
      if (units >= TOP) return KACHING.bid;
      if (units === 2) return KACHING.bid2 || KACHING.bid;
      return "";
    }

    /* Three shapes, exactly as Kaching's own widget writes them.

       - the main line: one single unit, carrying the deal, the product and the
         tier. Kaching refuses the bundle if this line has a quantity above one,
         which is why every further unit becomes a line of its own below.
       - every other paid unit: a bundled line, no tier, no product.
       - the gift: the id of the gift selector inside the three-item bar.

       Nothing here is a permission. It is a claim, and Kaching's function
       re-checks it against the deal before it prices anything. */
    function kachingMain(item, groupId, bar) {
      var value = {
        deal: KACHING.deal,
        pp: Number(item.productId) || 0,
        main: !0,
        v: KACHING.version,
        id: groupId,
        bid: bar,
      };
      if (KACHING.market) value.market = KACHING.market;
      return value;
    }

    function kachingExtra(groupId) {
      var value = {
        id: groupId,
        deal: KACHING.deal,
        v: KACHING.version,
        collectionBreaksProduct: !0,
      };
      if (KACHING.market) value.market = KACHING.market;
      return value;
    }

    function kachingGift(groupId) {
      var value = {
        id: groupId,
        deal: KACHING.deal,
        v: KACHING.version,
        gift: KACHING.gift,
      };
      if (KACHING.market) value.market = KACHING.market;
      return value;
    }

    function kachingStamp(props, value) {
      props[KACHING.property] = JSON.stringify(value);
      return props;
    }

    /* The third milestone is not a discount but a reward: a free Awrah Cover
       Legging. That is a different kind of thing from a percentage, so it keeps
       its own setting and the ladder below stays purely about percentages. */
    var GIFT = {
      at: parseInt(root.dataset.dmmGiftAt, 10) || 0,
      label: (root.dataset.dmmGiftLabel || "").trim(),
      name: (root.dataset.dmmGiftName || "").trim(),
      /* The colours the picker offers, as product ids. This is a sanity check,
         not a security boundary: the binding list is the allowlist inside the
         discount function, because that one is not written by the browser. */
      products: (root.dataset.dmmGiftProducts || "")
        .split(",")
        .map(function (id) {
          return String(id).trim();
        })
        .filter(Boolean),
    };

    /* The gift is earned at exactly the milestone count. The builder refuses a
       fourth paid item, so "at or above" and "exactly" are the same thing here,
       but the function checks for exactly and this matches it. */
    function giftOn(count) {
      return GIFT.at > 0 && count >= GIFT.at;
    }

    var MM_GIFT_PROPERTY = "_deen_mm_gift";
    var MM_GIFT_HANDLE_PROPERTY = "_deen_mm_gift_product";

    /* What the shopper has picked as their free legging, or null. Colour and
       size are two separate decisions, so this fills in two stages and is only
       usable once variantId is set. */
    var gift = null;

    function giftAllowed(productId) {
      return !GIFT.products.length || GIFT.products.indexOf(String(productId)) !== -1;
    }

    function giftComplete() {
      return !!(gift && gift.variantId);
    }

    /* The cart button only opens once the reward is actually specified. Below
       the milestone there is no gift to specify, so it opens on item count
       alone, which is what keeps the 1-item and 2-item purchases working. */
    function giftSatisfied() {
      return !giftOn(items.length) || giftComplete();
    }

    function giftSets() {
      return els.giftZone ? els.giftZone.querySelectorAll("[data-dmm-gift-set]") : [];
    }

    function clearGift() {
      gift = null;
      if (!els.giftZone) return;
      els.giftZone.querySelectorAll("[data-dmm-gift-swatch]").forEach(function (sw) {
        sw.setAttribute("aria-pressed", "false");
      });
      els.giftZone.querySelectorAll("[data-dmm-gift-size]").forEach(function (pill) {
        pill.setAttribute("aria-pressed", "false");
      });
      Array.prototype.forEach.call(giftSets(), function (set) {
        set.hidden = true;
      });
    }

    /* Picking a colour always drops the size: a size pill belongs to one
       colour's variants, so carrying it across would mean adding a variant the
       shopper never saw. */
    function pickGiftColour(sw) {
      var productId = sw.dataset.dmmProductId;
      if (!giftAllowed(productId)) {
        toast("That colour is not part of the free gift.");
        return;
      }
      els.giftZone.querySelectorAll("[data-dmm-gift-swatch]").forEach(function (other) {
        other.setAttribute("aria-pressed", other === sw ? "true" : "false");
      });
      els.giftZone.querySelectorAll("[data-dmm-gift-size]").forEach(function (pill) {
        pill.setAttribute("aria-pressed", "false");
      });
      Array.prototype.forEach.call(giftSets(), function (set) {
        set.hidden = set.dataset.dmmGiftSet !== String(productId);
      });
      gift = {
        productId: String(productId),
        handle: sw.dataset.dmmHandle || "",
        title: sw.dataset.dmmTitle || GIFT.name,
        colour: sw.dataset.dmmColour || "",
        image: sw.dataset.dmmThumb || "",
        variantId: null,
        size: "",
      };
      (render(), announce(gift.colour + " chosen for your free gift. Now choose a size."));
    }

    function pickGiftSize(pill) {
      if (!gift) return;
      var set = pill.closest("[data-dmm-gift-set]");
      if (!set || set.dataset.dmmGiftSet !== gift.productId) return;
      set.querySelectorAll("[data-dmm-gift-size]").forEach(function (other) {
        other.setAttribute("aria-pressed", other === pill ? "true" : "false");
      });
      ((gift.variantId = pill.dataset.dmmVariantId),
        (gift.size = pill.dataset.dmmLabel || ""),
        render(),
        announce("Free " + GIFT.name + ", " + gift.colour + ", size " + gift.size + " chosen."));
    }

    if (els.giftZone) {
      els.giftZone.querySelectorAll("[data-dmm-gift-swatch]").forEach(function (sw) {
        sw.addEventListener("click", function () {
          if (!sw.disabled) pickGiftColour(sw);
        });
      });
      els.giftZone.querySelectorAll("[data-dmm-gift-size]").forEach(function (pill) {
        pill.addEventListener("click", function () {
          if (!pill.disabled) pickGiftSize(pill);
        });
      });
    }

    /* The zone only exists at the milestone, and what it says depends on how
       far the shopper has got: colour, then size, then the result. */
    function renderGift() {
      if (!els.giftZone) return;
      var show = giftOn(items.length);
      els.giftZone.hidden = !show;
      if (!show) return;
      if (els.giftStep) {
        els.giftStep.textContent = !gift
          ? "Choose a colour"
          : giftComplete()
            ? ""
            : "Choose a size";
        els.giftStep.hidden = giftComplete();
      }
      if (els.giftChosen) {
        els.giftChosen.hidden = !giftComplete();
        if (giftComplete()) {
          (els.giftImg && gift.image && (els.giftImg.src = gift.image),
            els.giftImg && (els.giftImg.alt = gift.title || GIFT.name),
            els.giftColour && (els.giftColour.textContent = gift.colour),
            els.giftSize && (els.giftSize.textContent = "Size " + gift.size));
        }
      }
    }

    /* The discount is a ladder of percentages, read from one setting that looks
       like "2:10": two or more items take 10% off.
       A percentage needs no exchange rate and no price per market, so the same
       ladder is correct in every presentment currency, and it is exactly what
       the Kaching function does to each entitled line.

       A step may carry a second percentage, "2:12:10": the first is what the
       Kaching bar actually charges and is the only number this file calculates
       with, the second is the number the shopper is told. They differ only
       because Kaching rounds the finished bundle up to .99, which eats a little
       of the discount; charging slightly more than is promised keeps the promise
       true in every currency. Written with one number, both are that number. */
    var TIERS = (function () {
      var list = [];
      (root.dataset.dmmTiers || "").split(/[\n,;]+/).forEach(function (part) {
        var m = String(part)
          .trim()
          .match(/^(\d+)\s*[:=]\s*(\d+(?:[.,]\d+)?)(?:\s*[:=]\s*(\d+(?:[.,]\d+)?))?$/);
        if (!m) return;
        var min = parseInt(m[1], 10),
          pct = parseFloat(m[2].replace(",", ".")),
          show = m[3] == null ? pct : parseFloat(m[3].replace(",", "."));
        if (!(show > 0 && show < 100)) show = pct;
        if (min > 0 && pct > 0 && pct < 100) list.push({ min: min, pct: pct, show: show });
      });
      list.sort(function (a, b) {
        return a.min - b.min;
      });
      return list;
    })();

    /* How many items the top step needs. Drives the progress bar and nothing
       else: there is no maximum, a shopper may keep adding. */
    var TOP = (function () {
      var top = TIERS.length ? TIERS[TIERS.length - 1].min : MAX;
      return GIFT.at > top ? GIFT.at : top;
    })();

    function activeTier(count) {
      var found = null;
      for (var i = 0; i < TIERS.length; i++) if (count >= TIERS[i].min) found = TIERS[i];
      return found;
    }

    function nextTier(count) {
      for (var i = 0; i < TIERS.length; i++) if (TIERS[i].min > count) return TIERS[i];
      return null;
    }

    /* What the shopper gets for adding one more item: the next percentage step
       while there is one, and after that the free legging. Returning one shape
       for both keeps the copy, the dashed row and the bar on a single path. */
    function nextReward(count) {
      var tier = nextTier(count);
      if (tier) return { kind: "tier", at: tier.min, pct: tier.show };
      if (GIFT.at > count) return { kind: "gift", at: GIFT.at };
      return null;
    }

    /* The milestones on the progress bar: the first item, then one per step of
       the ladder. Positions come from the ladder itself, so the dots and the
       real discount can never drift apart. */
    var MILES = (function () {
      var list = [{ at: 1, label: "1 item" }];
      TIERS.forEach(function (tier) {
        if (tier.min > 1) list.push({ at: tier.min, label: pctText(tier.show) + "% off" });
      });
      if (GIFT.at > 1 && GIFT.label) list.push({ at: GIFT.at, label: GIFT.label });
      list.sort(function (a, b) {
        return a.at - b.at;
      });
      var top = list[list.length - 1].at || 1;
      return list.map(function (m) {
        return { at: m.at, label: m.label, pos: (m.at / top) * 100 };
      });
    })();

    /* Where the blue fill stops: exactly on the last milestone the shopper has
       reached, read from the milestones themselves, so the bar and the dots can
       never disagree by a pixel. */
    function fillPercent(count) {
      var pct = 0;
      MILES.forEach(function (m) {
        if (count >= m.at && m.pos > pct) pct = m.pos;
      });
      return Math.round(pct * 1000) / 1000;
    }

    function renderMiles(count) {
      if (!els.miles || !els.miles.length) return;
      var html = MILES.map(function (m) {
        return (
          '<li class="deen-mm-men__mile' +
          (count >= m.at ? " is-reached" : "") +
          '" style="left:' +
          m.pos +
          '%"><span class="deen-mm-men__mile-dot"></span><span class="deen-mm-men__mile-label">' +
          escHtml(m.label) +
          "</span></li>"
        );
      }).join("");
      Array.prototype.forEach.call(els.miles, function (node) {
        node.innerHTML = html;
      });
    }

    function pctText(pct) {
      return (Math.round(pct * 10) / 10).toString().replace(/\.0$/, "");
    }

    /* "2+ items 10% off", written from the ladder itself so the
       wording can never drift from what the cart applies. */
    function tiersLabel() {
      return TIERS.map(function (tier, i) {
        var last = i === TIERS.length - 1;
        return tier.min + (last ? "+" : " items") + " " + pctText(tier.show) + "% off";
      }).join(" \u00B7 ");
    }

    /* Copy for the discount line, so the offer is visible from the first view
       instead of only appearing once something is already in the bundle. */
    var DEAL_COPY = {
      intro: root.dataset.dmmDealIntro || "",
      progress: root.dataset.dmmDealProgress || "",
      bar: root.dataset.dmmDealBar || "",
      giftProgress: root.dataset.dmmGiftProgress || "",
      giftBar: root.dataset.dmmGiftBar || "",
      giftDone: root.dataset.dmmGiftDone || "",
    };

    function escHtml(value) {
      return String(value == null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    }

    function fillCopy(template, values) {
      return String(template || "").replace(
        /\{(count|left|pct|next|tiers|gift)\}/g,
        function (all, key) {
          return values[key] == null ? "" : String(values[key]);
        },
      );
    }

    /* Builds the discount line, keeping the percentage in its own span wherever
       the setting puts {pct} so it can be styled without hard-coding wording. */
    function dealMarkup(template, values) {
      var parts = String(template || "").split("{pct}"),
        html = "",
        i = 0;
      for (; i < parts.length; i++) {
        // The percent sign belongs with the number, so a template that writes
        // "{pct}% off" does not end up printing "10%% off".
        var raw = i > 0 ? String(parts[i]).replace(/^%/, "") : parts[i],
          text = fillCopy(raw, values);
        if (i > 0) {
          html +=
            '<span class="deen-mm-men__deal-price">' + escHtml(values.pct + "%") + "</span>";
        }
        if (text) html += '<span class="deen-mm-men__deal-text">' + escHtml(text) + "</span>";
      }
      return html;
    }

    function toast(message) {
      els.toast &&
        ((els.toast.textContent = message),
        els.toast.classList.add("is-visible"),
        window.clearTimeout(toastTimer),
        (toastTimer = window.setTimeout(function () {
          els.toast.classList.remove("is-visible");
        }, 2600)));
    }
    function announce(message) {
      els.live && (els.live.textContent = message);
    }
    // How far below the top of the window a family card has to land so that a
    // sticky theme header cannot cover it. Measured rather than assumed.
    function stickyOffset() {
      var pad = 16;
      var header = document.querySelector(
        "#shopify-section-header, .shopify-section-header, header",
      );
      if (header) {
        var position = window.getComputedStyle(header).position;
        if (position === "fixed" || position === "sticky") {
          pad += header.getBoundingClientRect().height;
        }
      }
      return pad;
    }

    // A tab picks a family and the page then brings that family's card into
    // view. On a phone the tabs sit above a full-width card, so without this
    // the shopper taps "Vests" and is still looking at the tab row.
    function scrollToTab(name) {
      var panel =
        name === "all" ? els.panels[0] : root.querySelector('[data-dmm-panel="' + name + '"]');
      if (!panel || panel.hidden) return;
      var reduce =
        window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      var top = window.pageYOffset + panel.getBoundingClientRect().top - stickyOffset();
      window.scrollTo({ top: top < 0 ? 0 : top, behavior: reduce ? "auto" : "smooth" });
    }

    function activateTab(name, scroll) {
      (Array.prototype.forEach.call(els.tabs, function (tab) {
        var on = tab.dataset.dmmTab === name;
        (tab.setAttribute("aria-selected", on ? "true" : "false"), (tab.tabIndex = on ? 0 : -1));
      }),
        Array.prototype.forEach.call(els.panels, function (panel) {
          panel.hidden = !(name === "all" || panel.dataset.dmmPanel === name);
        }),
        scroll && scrollToTab(name));
    }
    Array.prototype.forEach.call(els.tabs, function (tab) {
      (tab.addEventListener("click", function () {
        activateTab(tab.dataset.dmmTab, true);
      }),
        tab.addEventListener("keydown", function (event) {
          if (!(event.key !== "ArrowRight" && event.key !== "ArrowLeft")) {
            event.preventDefault();
            var list = Array.prototype.slice.call(els.tabs),
              i = list.indexOf(tab),
              next = list[(i + (event.key === "ArrowRight" ? 1 : list.length - 1)) % list.length];
            (next.focus(), activateTab(next.dataset.dmmTab, true));
          }
        }));
    });
    function closeSizePanel(card, focusTrigger) {
      var toggle = card.querySelector("[data-dmm-size-toggle]"),
        panel = card.querySelector("[data-dmm-size-panel]");
      !toggle ||
        !panel ||
        ((panel.hidden = !0),
        toggle.setAttribute("aria-expanded", "false"),
        focusTrigger && toggle.focus());
    }
    function closeAllSizePanels(except) {
      Array.prototype.forEach.call(els.cards, function (card) {
        card !== except && closeSizePanel(card, !1);
      });
    }
    function toggleSizePanel(card) {
      var toggle = card.querySelector("[data-dmm-size-toggle]"),
        panel = card.querySelector("[data-dmm-size-panel]");
      if (!(!toggle || !panel || toggle.disabled)) {
        var open = toggle.getAttribute("aria-expanded") === "true";
        (closeAllSizePanels(card),
          (panel.hidden = open),
          toggle.setAttribute("aria-expanded", open ? "false" : "true"));
      }
    }
    (document.addEventListener("click", function (event) {
      event.target.closest("[data-dmm-sizefield]") || closeAllSizePanels(null);
    }),
      root.addEventListener("keydown", function (event) {
        if (event.key === "Escape") {
          var field = event.target.closest("[data-dmm-card]");
          field && closeSizePanel(field, !0);
        }
      }));
    /* The bundle is keyed on the real variant, not on the product: the same
       joggers in S and in M are two different things to buy, so both may sit in
       the bundle at once. Only the exact same variant is refused twice. */
    /* Every addition is its own line with its own key, so the same product,
       colour and size can sit in the bundle twice (two of the same shorts plus
       one shirt is a valid bundle). Removal works per line, by key. */
    var lineSeq = 0;

    function countOfVariant(variantId) {
      var n = 0;
      for (var i = 0; i < items.length; i++) {
        if (String(items[i].variantId) === String(variantId)) n++;
      }
      return n;
    }

    function indexOfKey(key) {
      for (var i = 0; i < items.length; i++) {
        if (String(items[i].key) === String(key)) return i;
      }
      return -1;
    }

    function indexOfVariant(variantId) {
      for (var i = 0; i < items.length; i++) {
        if (String(items[i].variantId) === String(variantId)) return i;
      }
      return -1;
    }

    function productInBundle(productId) {
      for (var i = 0; i < items.length; i++) {
        if (String(items[i].productId) === String(productId)) return true;
      }
      return false;
    }

    function chosenVariantId(card) {
      var size = chosenSize(card);
      return size ? size.dataset.dmmVariantId : null;
    }

    function indexOfProduct(productId) {
      for (var i = 0; i < items.length; i++)
        if (String(items[i].productId) === String(productId)) return i;
      return -1;
    }
    function activeSet(card) {
      return card.querySelector("[data-dmm-colour-set]:not([hidden])") || card;
    }
    function chosenSize(card) {
      return activeSet(card).querySelector('[data-dmm-size][aria-pressed="true"]:not(:disabled)');
    }
    function modelName(card) {
      var el = card.querySelector(".deen-mm-men__name");
      return el ? el.textContent.trim() : card.dataset.dmmTitle || "";
    }
    function colourName(card) {
      var el = card.querySelector("[data-dmm-colour-name]");
      return el ? el.textContent.trim() : "";
    }
    function readCard(card) {
      var size = chosenSize(card);
      return size
        ? {
            productId: card.dataset.dmmProductId,
            handle: card.dataset.dmmHandle || "",
            variantId: size.dataset.dmmVariantId,
            title: card.dataset.dmmTitle || "",
            model: modelName(card),
            colour: colourName(card),
            image: card.dataset.dmmImage || "",
            group: card.dataset.dmmGroup || "",
            size: size.dataset.dmmLabel || "",
            hasSize: card.dataset.dmmHasSize === "true",
            price: parseInt(size.dataset.dmmPrice, 10) || 0,
            /* The variant's own compare-at, in the market currency, or 0 when the
               product has none. Only the summary uses it; every amount that is
               charged is still calculated from price above. */
            compareAt: parseInt(size.dataset.dmmCompare, 10) || 0,
          }
        : null;
    }
    function syncOneSize(card) {
      var set = activeSet(card);
      if (!set.querySelector("button[data-dmm-size]")) {
        var only = set.querySelector("[data-dmm-size]");
        if (only) {
          only.setAttribute("aria-pressed", "true");
          var label = card.querySelector("[data-dmm-size-label]"),
            field = card.querySelector("[data-dmm-sizefield]");
          (label && (label.textContent = "One size"), field && field.classList.add("is-chosen"));
        }
      }
    }
    function pressSize(card, wanted) {
      if (!wanted) return !1;
      var match = null;
      if (
        (activeSet(card)
          .querySelectorAll("button[data-dmm-size]:not([disabled])")
          .forEach(function (pill) {
            !match && pill.dataset.dmmLabel === wanted && (match = pill);
          }),
        !match)
      )
        return !1;
      match.setAttribute("aria-pressed", "true");
      var label = card.querySelector("[data-dmm-size-label]"),
        field = card.querySelector("[data-dmm-sizefield]");
      return (label && (label.textContent = wanted), field && field.classList.add("is-chosen"), !0);
    }
    function addCard(card) {
      /* The hard cap. A bundle is three paid items and the shopper is told so
         rather than silently ignored; the free legging is not one of them and
         never counts against this. */
      if (items.length >= MAX) {
        toast("Bundle full. Remove an item to swap it.");
        return;
      }
      var item = readCard(card);
      if (!item) {
        (toast("Choose a size first."), toggleSizePanel(card));
        return;
      }
      item.key = "l" + ++lineSeq;
      (items.push(item),
        render(),
        announce(
          item.title + ", size " + item.size + " added. " + items.length + " in your bundle.",
        ));
    }

    function removeLine(key) {
      var i = indexOfKey(key);
      if (i !== -1) {
        var removed = items.splice(i, 1)[0];
        (render(),
          announce(removed.title + " removed. " + items.length + " in your bundle."));
      }
    }
    function switchColour(card, sw) {
      if (!(!sw || sw.disabled)) {
        (card.querySelectorAll("[data-dmm-swatch]").forEach(function (other) {
          other.setAttribute("aria-pressed", other === sw ? "true" : "false");
        }),
          (card.dataset.dmmProductId = sw.dataset.dmmProductId),
          (card.dataset.dmmHandle = sw.dataset.dmmHandle || ""),
          (card.dataset.dmmTitle = sw.dataset.dmmTitle || ""),
          (card.dataset.dmmImage = sw.dataset.dmmThumb || ""),
          (card.dataset.dmmHasSize = sw.dataset.dmmHasSize || "true"),
          (card.dataset.dmmAvailable = sw.dataset.dmmAvailable || "true"));
        var img = card.querySelector("[data-dmm-media-img]");
        img &&
          (sw.dataset.dmmSrcset && (img.srcset = sw.dataset.dmmSrcset),
          sw.dataset.dmmLarge && (img.src = sw.dataset.dmmLarge),
          (img.alt = sw.dataset.dmmTitle || ""));
        var priceEl = card.querySelector("[data-dmm-price-value]");
        priceEl && sw.dataset.dmmPrice && (priceEl.textContent = sw.dataset.dmmPrice);
        var compareEl = card.querySelector("[data-dmm-compare-value]");
        compareEl &&
          (sw.dataset.dmmCompare
            ? ((compareEl.textContent = sw.dataset.dmmCompare), (compareEl.hidden = !1))
            : ((compareEl.textContent = ""), (compareEl.hidden = !0)));
        var colourEl = card.querySelector("[data-dmm-colour-name]");
        (colourEl && (colourEl.textContent = sw.dataset.dmmColour || ""),
          card.querySelectorAll("[data-dmm-size]").forEach(function (pill) {
            pill.setAttribute("aria-pressed", "false");
          }),
          card.querySelectorAll("[data-dmm-colour-set]").forEach(function (set) {
            set.hidden = set.dataset.dmmColourSet !== sw.dataset.dmmProductId;
          }));
        var field = card.querySelector("[data-dmm-sizefield]"),
          label = card.querySelector("[data-dmm-size-label]");
        (field && field.classList.remove("is-chosen"),
          label && (label.textContent = "Select size"),
          closeSizePanel(card, !1));
        var trigger = card.querySelector("[data-dmm-size-toggle]");
        (trigger && (trigger.disabled = card.dataset.dmmAvailable === "false"), syncOneSize(card));
        var idx = indexOfProduct(sw.dataset.dmmProductId);
        (idx !== -1 && pressSize(card, items[idx].size), render());
      }
    }
    Array.prototype.forEach.call(els.cards, function (card) {
      var toggle = card.querySelector("[data-dmm-size-toggle]");
      (toggle &&
        toggle.addEventListener("click", function (event) {
          (event.stopPropagation(), toggleSizePanel(card));
        }),
        card.querySelectorAll("[data-dmm-swatch]").forEach(function (sw) {
          sw.addEventListener("click", function (event) {
            (event.stopPropagation(), switchColour(card, sw));
          });
        }),
        card.querySelectorAll("[data-dmm-size]").forEach(function (size) {
          size.addEventListener("click", function (event) {
            if (!size.disabled) {
              (event.stopPropagation(),
                card.querySelectorAll("[data-dmm-size]").forEach(function (other) {
                  other.setAttribute("aria-pressed", other === size ? "true" : "false");
                }));
              var label = card.querySelector("[data-dmm-size-label]");
              label && (label.textContent = size.dataset.dmmLabel || "");
              var field = card.querySelector("[data-dmm-sizefield]");
              (field && field.classList.add("is-chosen"), closeSizePanel(card, !1));
              // Picking another size never rewrites a line that is already in
              // the bundle: it simply offers this size as a second addition.
              render();
            }
          });
        }),
        syncOneSize(card));
      var add = card.querySelector("[data-dmm-add]");
      add &&
        add.addEventListener("click", function () {
          if (add.disabled) return;
          addCard(card);
        });
    });
    /* Any price claim at all is gated on the display state, so "off" really
       means no claims anywhere. */
    function pricingOn() {
      return SAVINGS_MODE !== "off" && TIERS.length > 0;
    }

    function savingsUsable() {
      return pricingOn();
    }

    /* What this selection is actually offered. A percentage off the items in
       the bundle: no exchange rate is invented anywhere, the amount simply
       follows whatever the shopper's own currency already shows. */
    /* What this selection is actually offered. Kaching takes the percentage off
       each single unit and rounds that amount down, then charges it per unit,
       so a 10% cut on 30.95 is 3.09 and never 3.10. The page has to do exactly
       the same arithmetic, otherwise a bundle of three shirts reads a cent
       below what the cart charges. Summing per unit also means no exchange rate
       is invented anywhere: whatever the shopper's own currency already shows
       is what gets multiplied. */
    /* How many decimals this shopper's currency actually has. Yen and the other
       zero-decimal currencies have none, and Kaching leaves those bundles
       alone, so the rounding below has to leave them alone too. Read from Intl
       rather than from a hard-coded list, so a currency we have never seen is
       still handled correctly. */
    function minorDigits() {
      try {
        return new Intl.NumberFormat(void 0, {
          style: "currency",
          currency: activeCurrency(CURRENCY),
        }).resolvedOptions().maximumFractionDigits;
      } catch (e) {
        return 2;
      }
    }

    /* Kaching's own price rounding, repeated here so the page cannot promise a
       number the cart will not charge. Its rule, measured against the real cart
       in EUR, USD, GBP and MAD: take the bundle total up to the next whole unit
       and subtract one cent. 132.22 becomes 132.99, 94.50 becomes 94.99, and a
       total already on a whole unit drops a cent, so 81.00 becomes 80.99.

       This is set in the Kaching deal, not here. The theme setting only mirrors
       it; if one is changed the other has to change with it, or the page and
       the cart drift apart. */
    function roundBundle(cents) {
      if (KACHING.rounding !== "99" || !kachingReady()) return cents;
      if (minorDigits() === 0) return cents;
      return Math.ceil(cents / 100) * 100 - 1;
    }

    /* What one item is worth before any discount: its compare-at when the shop
       has one and it really is higher, otherwise the price the shopper pays for
       it today. The summary totals use this; the cart never does, so a missing
       or stale compare-at can only make the advertised saving smaller, never
       the amount charged different. */
    /* Guards the two ways this can go wrong: no discount at all, and a compare-at
       total that is somehow not above the bundle price. Both end at zero rather
       than at a negative saving. */
    function savingAgainstList(listTotal, saving) {
      if (!saving || !listTotal) return { amount: 0, percent: 0 };
      var amount = listTotal - saving.bundle;
      if (amount <= 0) return { amount: 0, percent: 0 };
      return { amount: amount, percent: (amount / listTotal) * 100 };
    }

    function listValue(item) {
      return item.compareAt > item.price ? item.compareAt : item.price;
    }

    /* The summary's "Items total": the regular value of the paid items. The free
       legging is not an item here, so it can never inflate the saving. */
    function listTotalOf(list) {
      return list.reduce(function (sum, item) {
        return sum + listValue(item);
      }, 0);
    }

    function dealFor(count, regularCents) {
      if (!pricingOn() || !regularCents) return null;
      var tier = activeTier(count);
      if (!tier) return null;
      var save = items.reduce(function (sum, item) {
        return sum + Math.floor((item.price * tier.pct) / 100);
      }, 0);
      if (save <= 0) return null;
      var bundle = roundBundle(regularCents - save);
      save = regularCents - bundle;
      if (save <= 0) return null;
      return {
        bundle: bundle,
        save: save,
        // What the shopper is told. The money above is the truth; this number
        // only labels it, and never rises above what the bundle really saves.
        percent: tier.show,
        source: "tier",
      };
    }

    function summarySubText(count) {
      var el = els.summarySub;
      if (!el) return null;
      if (count >= TOP) return el.dataset.dmmSubDone || "Your bundle is ready.";
      // The attribute is data-dmm-sub-0; a dash before a digit is not camel
      // cased by the dataset API, so the key really is "dmmSub-0".
      var fromSettings = el.dataset["dmmSub-" + count] || el.dataset["dmmSub" + count];
      if (fromSettings) return fromSettings;
      var step = nextReward(count);
      if (!step) return el.dataset.dmmSubDone || "Your bundle is ready.";
      var left = step.at - count;
      var what = step.kind === "gift" ? "a free " + (GIFT.name || "gift") : pctText(step.pct) + "% off";
      return "Add " + left + " more item" + (left === 1 ? "" : "s") + " for " + what + ".";
    }
    /* The dashed row under the bundle: what the next step gives, so the offer
       sits right where the shopper is looking instead of only in the header. */
    function nextSlotMarkup(step, count) {
      var left = step.at - count;
      var unlocks =
        step.kind === "gift"
          ? "Unlocks a FREE " + escHtml(GIFT.name || "gift")
          : "Unlocks " + pctText(step.pct) + "% off your whole bundle";
      return (
        '<div class="deen-mm-men__slot deen-mm-men__slot--empty"><div class="deen-mm-men__slot-media">+</div>' +
        '<div class="deen-mm-men__slot-info"><div class="deen-mm-men__slot-title">Add ' +
        left +
        " more item" +
        (left === 1 ? "" : "s") +
        '</div><div class="deen-mm-men__slot-meta">' +
        unlocks +
        "</div></div></div>"
      );
    }
    function slotMarkup(item, index) {
      if (!item) {
        return (
          '<div class="deen-mm-men__slot deen-mm-men__slot--empty"><div class="deen-mm-men__slot-media">+</div>' +
          '<div class="deen-mm-men__slot-info"><div class="deen-mm-men__slot-title">Nothing selected yet</div>' +
          '<div class="deen-mm-men__slot-meta">Pick a colour and size below</div></div></div>'
        );
      }
      var meta = [];
      return (
        item.colour && meta.push(item.colour),
        item.hasSize && item.size && meta.push("Size " + item.size),
        meta.push(money(item.price, CURRENCY)),
        '<div class="deen-mm-men__slot"><div class="deen-mm-men__slot-media">' +
          (item.image
            ? '<img src="' + item.image + '" alt="" loading="lazy" width="56" height="70">'
            : "") +
          '</div><div class="deen-mm-men__slot-info"><div class="deen-mm-men__slot-title">' +
          (item.model || item.title) +
          '</div><div class="deen-mm-men__slot-meta">' +
          meta.join(" \xB7 ") +
          '</div></div><button type="button" class="deen-mm-men__remove" data-dmm-remove="' +
          item.key +
          '" aria-label="Remove ' +
          item.title +
          ' from bundle">&times;</button></div>'
      );
    }
    /* One item is already a real purchase, so the cart button is live from the
       first selection and says what it will add. */
    /* There is no minimum any more: one item is already a real purchase, so the
       cart button is live from the first selection and says what it will add. */
    function ctaLabel() {
      if (!items.length) return "Select an item";
      /* The bundle is complete but the reward is not, so the button says what
         is missing instead of just sitting there greyed out. */
      if (giftOn(items.length) && !giftComplete()) return "Choose your free legging";
      if (giftOn(items.length)) return "Add bundle to cart";
      return "Add " + items.length + " item" + (items.length === 1 ? "" : "s") + " to cart";
    }
    function render() {
      var regular = items.reduce(function (sum, item) {
          return sum + item.price;
        }, 0),
        /* Two totals on purpose. "regular" is what the items cost today and is
           the only number the discount arithmetic touches, so it stays in step
           with the cart. "listTotal" is the regular value the summary strikes
           through, built from compare-at. */
        listTotal = listTotalOf(items),
        complete = items.length >= TOP,
        step = nextReward(items.length),
        saving = dealFor(items.length, regular),
        /* What the summary advertises: the difference between that struck-through
           regular value and the price the cart will really charge, with the
           percentage read off those same two numbers rather than the tier, so the
           line can never claim more than the figures above it show. */
        listSaving = savingAgainstList(listTotal, saving),
        /* With no bundle discount there is nothing to strike through, so the
           total shows what the shopper would actually pay. Showing the regular
           value there would read as the price and overstate it. */
        shownTotal = saving ? listTotal : regular,
        percent = fillPercent(items.length);
      (Array.prototype.forEach.call(root.querySelectorAll("[data-dmm-items-word]"), function (node) {
        node.textContent = items.length === 1 ? "item" : "items";
      }),
      Array.prototype.forEach.call(els.count, function (node) {
        node.textContent = items.length;
      }),
        Array.prototype.forEach.call(els.total, function (node) {
          node.textContent = money(shownTotal, CURRENCY);
        }),
        Array.prototype.forEach.call(els.fills, function (node) {
          node.style.width = percent + "%";
        }),
        renderMiles(items.length),
        Array.prototype.forEach.call(els.tracks, function (node) {
          (node.setAttribute("aria-valuenow", String(Math.min(items.length, TOP))),
            node.setAttribute(
              "aria-valuetext",
              items.length + " item" + (items.length === 1 ? "" : "s") + " selected",
            ));
        }));
      var sub = summarySubText(items.length);
      if (
        (els.summarySub && sub && (els.summarySub.textContent = sub),
        root.classList.toggle("is-complete", complete),
        root.classList.toggle("is-saving", !!saving),
        Array.prototype.forEach.call(els.savings, function (node) {
          node.hidden = !saving;
        }),
        renderGift(),
        saving &&
          (Array.prototype.forEach.call(els.bundlePrice, function (n) {
            n.textContent = money(saving.bundle, CURRENCY);
          }),
          Array.prototype.forEach.call(els.saveAmount, function (n) {
            n.textContent = money(listSaving.amount, CURRENCY);
          }),
          Array.prototype.forEach.call(els.savePercent, function (n) {
            n.textContent = "(" + pctText(listSaving.percent) + "%)";
          })),
        els.slots)
      ) {
        for (var html = "", i = 0; i < items.length; i++) html += slotMarkup(items[i], i);
        if (step) html += nextSlotMarkup(step, items.length);
        else if (!items.length) html += slotMarkup(null, 0);
        els.slots.innerHTML = html;
      }
      /* The discount ladder, up front. Before anything is chosen it writes out
         the whole ladder; while choosing it names only the next step. Once the
         top step is reached there is nothing left to unlock, so it steps aside
         and the savings block states what is actually taken off. */
      var showDeal = pricingOn() && !!step,
        dealValues = step
          ? {
              left: step.at - items.length,
              pct: step.kind === "tier" ? pctText(step.pct) : "",
              next: step.at,
              count: items.length,
              tiers: tiersLabel(),
              gift: GIFT.name,
            }
          : { count: items.length, tiers: tiersLabel(), gift: GIFT.name };
      if (els.deal) {
        els.deal.hidden = !showDeal;
        if (showDeal && els.dealBody) {
          els.dealBody.innerHTML = dealMarkup(
            !items.length
              ? DEAL_COPY.intro
              : step.kind === "gift"
                ? DEAL_COPY.giftProgress
                : DEAL_COPY.progress,
            dealValues,
          );
        }
      }
      /* The phone bar shows what is actually in the bundle: a thumbnail per
         item with its size and a remove control, plus one dashed placeholder
         while there is still a step left to unlock. */
      if (els.barSlots) {
        var barSlots = items.length + (step ? 1 : 0) || 1;
        for (var bar = "", j = 0; j < barSlots; j++) {
          var it = items[j];
          bar +=
            '<div class="deen-mm-men__bar-slot' + (it ? " is-filled" : "") + '">' +
            (it
              ? (it.image ? '<img src="' + it.image + '" alt="" loading="lazy">' : "") +
                (it.hasSize && it.size
                  ? '<span class="deen-mm-men__bar-slot-size">' +
                    // "8-10 Years" would only truncate on a 44px chip.
                    it.size.replace(/\s*years?$/i, "Y") +
                    "</span>"
                  : "") +
                '<button type="button" class="deen-mm-men__bar-slot-remove" data-dmm-remove="' +
                it.key +
                '" aria-label="Remove ' +
                it.title +
                ' from bundle">&times;</button>'
              : '<span class="deen-mm-men__bar-slot-num">' + (j + 1) + "</span>") +
            "</div>";
        }
        els.barSlots.innerHTML = bar;
      }
      if (els.barMeta)
        if (showDeal)
          els.barMeta.innerHTML = dealMarkup(
            step.kind === "gift" ? DEAL_COPY.giftBar : DEAL_COPY.bar,
            dealValues,
          );
        else if (giftOn(items.length) && DEAL_COPY.giftDone)
          els.barMeta.innerHTML = dealMarkup(DEAL_COPY.giftDone, dealValues);
        else if (!items.length) els.barMeta.textContent = "Add more, pay less";
        else if (saving)
          els.barMeta.innerHTML =
            '<span class="deen-mm-men__bar-prices"><s>' +
            money(listTotal, CURRENCY) +
            "</s> <strong>" +
            money(saving.bundle, CURRENCY) +
            '</strong></span><span class="deen-mm-men__bar-savetext">Save ' +
            money(listSaving.amount, CURRENCY) +
            "</span>";
        else els.barMeta.textContent = money(shownTotal, CURRENCY);
      (els.barSave &&
        ((els.barSave.hidden = !saving),
        saving && (els.barSave.textContent = "Save " + money(listSaving.amount, CURRENCY))),
        Array.prototype.forEach.call(els.ctas, function (cta) {
          /* While the reward is still unchosen the button stays live on purpose.
             It reads "Choose your free legging", and tapping it walks the
             shopper down to the picker instead of sitting there greyed out
             with nothing happening. It still refuses to add: addToCart turns
             back at the same gate. */
          ((cta.disabled = !items.length || busy),
            cta.classList.toggle("is-waiting", !busy && !giftSatisfied()),
            (cta.textContent = busy ? "Adding\u2026" : ctaLabel()));
        }),
        Array.prototype.forEach.call(els.cards, function (card) {
          var chosenVariant = chosenVariantId(card),
            variantCount = chosenVariant ? countOfVariant(chosenVariant) : 0,
            variantOn = variantCount > 0,
            on = productInBundle(card.dataset.dmmProductId),
            available = card.dataset.dmmAvailable !== "false",
            add = card.querySelector("[data-dmm-add]");
          if (
            (card.classList.toggle("is-selected", on),
            card.classList.toggle("is-unavailable", !available),
            card.querySelectorAll("[data-dmm-swatch]").forEach(function (sw) {
              var inBundle = indexOfProduct(sw.dataset.dmmProductId) !== -1,
                colour = sw.dataset.dmmColour || "";
              (sw.classList.toggle("is-in-bundle", inBundle),
                (sw.title = inBundle ? colour + " (in your bundle)" : colour),
                sw.setAttribute(
                  "aria-label",
                  colour +
                    (sw.dataset.dmmAvailable === "false" ? " (sold out)" : "") +
                    (inBundle ? " (in your bundle)" : ""),
                ));
            }),
            !!add)
          ) {
            if (!available) {
              ((add.dataset.dmmUnavailable = "true"),
                (add.textContent = "Sold out"),
                (add.disabled = !0),
                add.classList.remove("is-selected"),
                add.setAttribute("aria-pressed", "false"));
              return;
            }
            delete add.dataset.dmmUnavailable;
            /* Once the bundle holds its three paid items every product button
               says so and stops accepting a fourth. The shopper removes one to
               swap; nothing is added behind their back. */
            if (items.length >= MAX) {
              ((add.textContent = "Bundle full"),
                (add.disabled = !0),
                add.classList.toggle("is-selected", variantOn),
                add.setAttribute("aria-pressed", variantOn ? "true" : "false"));
              return;
            }
            (add.classList.toggle("is-selected", variantOn),
              add.setAttribute("aria-pressed", variantOn ? "true" : "false"),
              variantOn
                ? ((add.textContent = "Add another (" + variantCount + " in bundle)"),
                  (add.disabled = !1))
                : ((add.textContent = "Add to bundle"), (add.disabled = !chosenVariant)));
          }
        }),
        els.bar && els.bar.classList.add("is-visible"),
        Array.prototype.forEach.call(els.done, function (node) {
          node.hidden = !0;
        }));
    }
    els.slots &&
      els.slots.addEventListener("click", function (event) {
        var button = event.target.closest("[data-dmm-remove]");
        button && removeLine(button.dataset.dmmRemove);
      });

    els.barSlots &&
      els.barSlots.addEventListener("click", function (event) {
        var button = event.target.closest("[data-dmm-remove]");
        button && removeLine(button.dataset.dmmRemove);
      });
    function refreshCartUi(sections) {
      var bubble = document.querySelector("#cart-icon-bubble"),
        html = sections && sections["cart-icon-bubble"];
      if (bubble && html) {
        var doc = new DOMParser().parseFromString(html, "text/html"),
          src =
            doc.querySelector("#shopify-section-cart-icon-bubble") ||
            doc.querySelector(".shopify-section") ||
            doc.querySelector("#cart-icon-bubble") ||
            doc.body;
        src && (bubble.innerHTML = src.innerHTML);
      }
      try {
        document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: !0 }));
      } catch (e) {}
      if (typeof window.upcartRefreshCart == "function")
        try {
          window.upcartRefreshCart();
        } catch (e) {}
      if (typeof window.upcartOpenCart == "function")
        try {
          window.upcartOpenCart();
        } catch (e) {}
    }
    function cartMessage() {
      return "Added to your cart. You can keep building.";
    }

    /* Only the builder is cleared, never the cart: the shopper can start a
       second bundle straight away and it is added alongside the first. */
    function resetBuilder() {
      items = [];
      clearGift();
      Array.prototype.forEach.call(els.cards, function (card) {
        card.querySelectorAll("[data-dmm-size]").forEach(function (pill) {
          pill.setAttribute("aria-pressed", "false");
        });
        var label = card.querySelector("[data-dmm-size-label]");
        if (label) label.textContent = "Select size";
        var field = card.querySelector("[data-dmm-sizefield]");
        if (field) field.classList.remove("is-chosen");
        closeSizePanel(card, false);
      });
      render();
    }

    function addToCart() {
      if (giftOn(items.length) && !giftComplete()) {
        (toast("Choose the colour and size of your free legging first."),
          els.giftZone && els.giftZone.scrollIntoView({ block: "center", behavior: "smooth" }));
        return;
      }
      if (!(busy || !items.length)) {
        ((busy = !0), render());
        var payload = {
          items: (function () {
            var lines = [];
            var at = {};
            var bar = kachingReady() ? kachingBar(items.length) : "";
            var groupId = bar ? kachingGroupId() : null;
            var mmGroup = mmGroupId();
            if (groupId) {
              /* Kaching's own widget sends the first unit on a line of its own
                 and every further unit on a bundled line. It refuses the tier
                 if the tier-carrying line holds more than one unit, so three of
                 the same size must not be collapsed here. */
              var head = items[0];
              lines.push({
                id: Number(head.variantId),
                quantity: 1,
                properties: kachingStamp(
                  (function () {
                    var p = {};
                    p[MM_GROUP_PROPERTY] = mmGroup;
                    return p;
                  })(),
                  kachingMain(head, groupId, bar),
                ),
              });
              items.slice(1).forEach(function (item) {
                var id = Number(item.variantId);
                if (at[id] === undefined) {
                  at[id] = lines.length;
                  lines.push({
                    id: id,
                    quantity: 1,
                    properties: kachingStamp(
                      (function () {
                        var p = {};
                        p[MM_GROUP_PROPERTY] = mmGroup;
                        return p;
                      })(),
                      kachingExtra(groupId),
                    ),
                  });
                } else {
                  lines[at[id]].quantity += 1;
                }
              });
            } else
              items.forEach(function (item) {
                var id = Number(item.variantId);
                if (at[id] === undefined) {
                  at[id] = lines.length;
                  var props = {};
                  props[MM_GROUP_PROPERTY] = mmGroup;
                  lines.push({ id: id, quantity: 1, properties: props });
                } else {
                  /* The same variant picked twice stays one line with quantity
                     2, which is what the function counts: units, not lines. */
                  lines[at[id]].quantity += 1;
                }
              });
            /* The gift travels as a real variant line with a real price, not a
               fake nothing: the discount function is what makes it free, and it
               re-derives the right to that from the paid units in this same
               group. The flag below is a claim, not a permission, and the
               Kaching property names the gift selector inside the three-item
               bar so that function knows which line to zero. */
            if (giftOn(items.length) && gift && gift.variantId) {
              var giftProps = {};
              giftProps[MM_GROUP_PROPERTY] = mmGroup;
              giftProps[MM_GIFT_PROPERTY] = "1";
              if (gift.handle) giftProps[MM_GIFT_HANDLE_PROPERTY] = gift.handle;
              if (groupId && KACHING.gift) kachingStamp(giftProps, kachingGift(groupId));
              lines.push({ id: Number(gift.variantId), quantity: 1, properties: giftProps });
            }
            return lines;
          })(),
          sections: ["cart-icon-bubble"],
          sections_url: window.location.pathname,
        };
        fetch(
          window.Shopify && window.Shopify.routes && window.Shopify.routes.root
            ? window.Shopify.routes.root + "cart/add.js"
            : "/cart/add.js",
          {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify(payload),
          },
        )
          .then(function (response) {
            return response.json().then(function (data) {
              if (!response.ok) throw data;
              return data;
            });
          })
          .then(function (data) {
            ((busy = !1),
              resetBuilder(),
              refreshCartUi(data.sections),
              Array.prototype.forEach.call(els.done, function (node) {
                node.hidden = !1;
              }),
              toast(cartMessage()),
              announce(cartMessage()));
          })
          .catch(function (error) {
            ((busy = !1), render());
            var message =
              (error && (error.description || error.message)) ||
              "We could not add this selection. Please try again.";
            (toast(message),
              window.console && window.console.error("[deen-mm-men] cart/add failed", error));
          });
      }
    }
    return (
      Array.prototype.forEach.call(els.ctas, function (cta) {
        cta.addEventListener("click", addToCart);
      }),
      render(),
      els.bar && els.bar.classList.add("is-visible"),
      (root.__dmm = {
        render: render,
        addToCart: addToCart,
        maxItems: MAX,
        getGift: function () {
          return gift ? JSON.parse(JSON.stringify(gift)) : null;
        },
        getItems: function () {
          return items.slice();
        },
        savingsUsable: savingsUsable,
        cartUrl: CART_URL,
      })
    );
  }
  function boot() {
    document.querySelectorAll("[data-dmm-root]").forEach(init);
  }
  return (
    document.readyState === "loading"
      ? document.addEventListener("DOMContentLoaded", boot)
      : boot(),
    document.addEventListener("shopify:section:load", function (event) {
      event.target && event.target.querySelectorAll("[data-dmm-root]").forEach(init);
    }),
    {
      init: init,
      boot: boot,
      money: money,
    }
  );
})();
